/**
 * Profit —— 淨利分配（P2-1 試算 ＋ **P2-2 過帳**；`decisions/006`／`010`）
 * ⚠️ 上半部（試算）＝只讀；下半部（`DistributionPanel`）＝真正過帳（要鎖帳、一期一次）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import { api, currentPeriod, fmtMoney, type Meta, type ProfitTrial } from '../lib/api';
import DistributionPanel from '../components/DistributionPanel';

export default function Profit({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [period, setPeriod] = useState(currentPeriod());
  const [split, setSplit] = useState('15');
  const [parties, setParties] = useState('');
  const [r, setR] = useState<ProfitTrial | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api.profit({ entityId, period, split, parties })
      .then(setR)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId, period, split, parties]);

  useEffect(() => { load(); }, [load]);

  return (
    <main className="page">
      <TopBar title={t('profit.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              <option value="">{t('profit.all_entities')}</option>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.month')}</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('profit.ai85_pct')}</span>
            <input inputMode="decimal" value={split} onChange={(e) => setSplit(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('profit.parties')}</span>
            <input
              value={parties} onChange={(e) => setParties(e.target.value)}
              placeholder={t('profit.parties_hint')}
            />
          </label>
        </div>
        <p className="note">{t('profit.readonly_note')}</p>
        {err && <p className="err">{err}</p>}

        {r && (
          <div className="totals">
            <div><small>{t('home.income')}</small><b className="pos">{fmtMoney(r.income_cents)}</b></div>
            <div><small>{t('home.expense')}</small><b className="neg">{fmtMoney(r.expense_cents)}</b></div>
            <div><small>{t('profit.net')}</small>
              <b className={r.net_cents >= 0 ? 'pos' : 'neg'}>{fmtMoney(r.net_cents)}</b>
            </div>
          </div>
        )}
      </section>

      {r && r.byEntity.length > 1 && (
        <section className="card">
          <h2>{t('profit.by_entity')}</h2>
          <table className="ledger">
            <thead><tr><th>{t('expense.entity')}</th><th className="num">{t('home.income')}</th>
              <th className="num">{t('home.expense')}</th><th className="num">{t('profit.net')}</th><th>{t('periods.status')}</th></tr></thead>
            <tbody>
              {r.byEntity.map((e) => (
                <tr key={e.entityId}>
                  <td>{e.name}</td>
                  <td className="num">{fmtMoney(e.income_cents)}</td>
                  <td className="num">{fmtMoney(e.expense_cents)}</td>
                  <td className={`num ${e.net_cents >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(e.net_cents)}</td>
                  <td><span className={`tag ${e.locked ? '' : 'warn'}`}>{e.locked ? t('periods.locked') : t('periods.open')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {r && (
        <section className="card">
          <h2>{t('profit.allocation')}</h2>
          {r.allocation.length === 0 && <p className="note">{t('profit.no_allocation')}</p>}
          {r.allocation.length > 0 && (
            <table className="ledger">
              <thead>
                <tr><th>{t('profit.party')}</th><th className="num">%</th><th className="num">{t('detail.amount')}</th></tr>
              </thead>
              <tbody>
                {r.allocation.map((a) => (
                  <tr key={`${a.party}-${a.pct}`}>
                    <td>{a.party}</td>
                    <td className="num">{a.pct}</td>
                    <td className="num">{fmtMoney(a.amount_cents)}</td>
                  </tr>
                ))}
                <tr>
                  <td><b>{t('profit.total')}</b></td>
                  <td className="num" />
                  <td className="num"><b>{fmtMoney(r.allocation.reduce((s, a) => s + a.amount_cents, 0))}</b></td>
                </tr>
              </tbody>
            </table>
          )}
          {r.warnings.length > 0 && (
            <ul className="warnlist">
              {r.warnings.map((w) => <li key={w}>⚠️ {w}</li>)}
            </ul>
          )}
          <p className="note">{t('profit.posting_disabled')}</p>
        </section>
      )}

      {/* P2-2 過帳（政策已拍板：`decisions/010`）*/}
      <section className="card">
        <h2>{t('dist.heading')}</h2>
        <DistributionPanel meta={meta} />
      </section>
    </main>
  );
}
